/**
 * Which edge ChatContainerReader.scrollChatBox pins to after a repaint.
 *
 *   grounding   -- the held opening beat, centred; pinning down scrolled it out
 *   readingLanded -- the reading is up (summary seen or not): the LAST card
 *                  leads, clean, nothing of the card before it cut off above
 *   postReading -- the post-reading menu (Rate / Share) is showing too: the
 *                  last card still leads, with Rate / Share right under it.
 *                  Not the top: a resumed reading replays every card, and the
 *                  top would be the Past card with the buttons a screen away
 *   rateOpen    -- the crystal-ball row has opened under the buttons; bring it up
 *   talking     -- "Talk it through" is open: the latest exchange leads (her
 *                  line and the Mystic's answer), not the cards above it
 */
export function chatPinEdge({ grounding = false, readingLanded = false, postReading = false, rateOpen = false,
  talking = false } = {}) {
  if (grounding) return 'top'
  if (talking) return 'last-message'
  if ((readingLanded || postReading) && !rateOpen) return 'last-card'
  return 'bottom'
}

/**
 * How far to scroll a pane further down so the post-reading menu (Rate /
 * Share) sits wholly inside it, with a small margin. 0 when it already fits.
 * Never so far that the menu's own top leaves the pane.
 */
export function menuOverflow(menuRect, paneRect, margin = 12) {
  const below = menuRect.bottom + margin - paneRect.bottom
  if (below <= 0) return 0
  return Math.max(0, Math.min(below, menuRect.top - paneRect.top))
}

/**
 * Paced draws (2026-10-23): while cards are drawn the chat pins to the bottom,
 * so the newest reveal is in view. A pane shorter than the reveal's panel
 * showed its foot and cut off its pre-line ("For the Past card, you
 * selected …"). How far to scroll back up so the panel's top shows, under
 * `headroom` px. 0 when it already does.
 */
export function revealTopShortfall(revealRect, paneRect, headroom = 8) {
  return Math.max(0, paneRect.top + headroom - revealRect.top)
}

/**
 * Where a pulled card that just landed puts the chat (gift card order,
 * 2026-10-29).
 *   'card' -- Talk it through: the panel leads on its own, its top under the
 *             pane's edge, with room made under it while its words come.
 *   'line' -- her card in the gift room: it lands UNDER the Mystic's lines
 *             (the first message, "Hold this question…"), never above them.
 *             The line before it leads and no room is added under it, so when
 *             the bridge and the chips come nothing pops in above the card.
 */
export function talkCardLead(card) {
  return card && card.kind === 'gift' ? 'line' : 'card'
}

/**
 * The scrollTop that puts `leadTop` `headroom` px under the pane's top
 * (`paneTop`), stopped at the chat's real bottom: no room is added, so a
 * short chat simply shows everything.
 */
export function leadScrollTop({ scrollTop = 0, scrollHeight = 0, clientHeight = 0, leadTop = 0, paneTop = 0,
  headroom = 12 } = {}) {
  const want = scrollTop + (leadTop - paneTop - headroom)
  const max = Math.max(0, scrollHeight - clientHeight)
  return Math.max(0, Math.min(max, want))
}
